import React, { useEffect, useState, useCallback } from 'react';
import { reportsAPI, staffAPI, api } from '../services/api';
import { SIMULATE_CHANGED_EVENT } from '../services/simulate';
import './CaseloadContactsReport.css';

function csvEscape(val) {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * Caseload Contacts report (legacy SC Report option 6 – Export Caseload Contacts).
 * Primary contacts for open clients assigned to the selected Service Coordinator.
 * View in table and/or export CSV.
 */
export default function CaseloadContactsReport({ embedded = false, refreshKey = 0, tenantCode = null }) {
  const [staffId, setStaffId] = useState('');
  const [staffOptions, setStaffOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roleCode, setRoleCode] = useState(null);
  const [myStaffId, setMyStaffId] = useState(null);
  const [staffDisplayName, setStaffDisplayName] = useState(null);
  const [tenantCodeFromWhoami, setTenantCodeFromWhoami] = useState(null);

  const fetchWhoami = useCallback(async () => {
    try {
      const res = await api.get('/whoami');
      const code = res.data?.membership?.role_code || null;
      const sid = res.data?.user?.staff_id ?? null;
      setRoleCode(code);
      setMyStaffId(sid != null ? String(sid) : null);
      setStaffDisplayName(res.data?.staff_display_name ?? null);
      setTenantCodeFromWhoami(res.data?.tenant?.code ?? null);
    } catch {
      setRoleCode(null);
      setMyStaffId(null);
      setStaffDisplayName(null);
      setTenantCodeFromWhoami(null);
    }
  }, []);

  useEffect(() => {
    fetchWhoami();
  }, [fetchWhoami]);

  useEffect(() => {
    const handler = () => fetchWhoami();
    window.addEventListener(SIMULATE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(SIMULATE_CHANGED_EVENT, handler);
  }, [fetchWhoami]);

  useEffect(() => {
    if (roleCode === 'SERVICE_COORDINATOR') return;
    let mounted = true;
    staffAPI
      .getAll({ active: 'true' })
      .then((res) => {
        if (!mounted) return;
        const list = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
        setStaffOptions(list);
      })
      .catch(() => {
        if (!mounted) return;
        setStaffOptions([]);
      });
    return () => { mounted = false; };
  }, [tenantCode, roleCode]);

  const runReport = () => {
    setError(null);
    const effectiveStaffId = roleCode === 'SERVICE_COORDINATOR' && myStaffId ? myStaffId : (staffId || '').trim();
    if (!effectiveStaffId) {
      setError('Please select a Service Coordinator (staff).');
      return;
    }
    setLoading(true);
    reportsAPI
      .get('caseload-contacts', { staff_id: effectiveStaffId })
      .then((res) => {
        setRows(res.data?.rows || []);
        setError(null);
      })
      .catch((err) => {
        setRows([]);
        setError(err.response?.data?.error || 'Failed to load caseload contacts.');
      })
      .finally(() => setLoading(false));
  };

  const downloadCsv = () => {
    const headers = ['primary_contact', 'client_name', 'phone', 'email'];
    const lines = [
      headers.join(','),
      ...rows.map((r) => headers.map((h) => csvEscape(r[h])).join(',')),
    ];
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'caseload-contacts.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  useEffect(() => {
    if (refreshKey > 0) runReport();
  }, [refreshKey]);

  const isSc = roleCode === 'SERVICE_COORDINATOR';
  const titleSuffix = tenantCode || tenantCodeFromWhoami ? ` — ${tenantCode || tenantCodeFromWhoami}` : '';
  const scDisplayName = staffDisplayName || (myStaffId ? `ID ${myStaffId}` : null);
  const hasResults = rows.length > 0;

  return (
    <div className="caseload-contacts-report">
      <h2 className="caseload-contacts-title">Caseload Contacts{titleSuffix}</h2>
      <p className="caseload-contacts-hint">
        Primary contacts for <strong>open</strong> clients assigned to the selected Service Coordinator. Select a staff member and click <strong>Run</strong> to view; then export to CSV if needed.
      </p>
      {isSc && scDisplayName && (
        <p className="caseload-contacts-context" aria-live="polite">
          Staff / Service Coordinator: {scDisplayName}
        </p>
      )}
      <div className="caseload-contacts-filters">
        {!isSc && (
          <div className="caseload-contacts-filter-row">
            <label htmlFor="cc-staff">Service Coordinator</label>
            <select
              id="cc-staff"
              value={staffId}
              onChange={(e) => setStaffId(e.target.value)}
              aria-required="true"
            >
              <option value="">Select staff…</option>
              {staffOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {[s.last_name, s.first_name].filter(Boolean).join(', ')}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="caseload-contacts-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={runReport}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Run'}
          </button>
          <button
            type="button"
            className="btn btn-primary caseload-contacts-download-btn"
            onClick={downloadCsv}
            disabled={rows.length === 0}
          >
            Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="caseload-contacts-error" role="alert">
          {error}
        </div>
      )}

      {hasResults && (
        <div className="caseload-contacts-results">
          <p className="caseload-contacts-count">
            {rows.length} contact{rows.length !== 1 ? 's' : ''}.
          </p>
          <div className="caseload-contacts-table-wrap">
            <table className="caseload-contacts-table">
              <thead>
                <tr>
                  <th>Primary contact</th>
                  <th>Client</th>
                  <th>Phone</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.primary_contact ?? '—'}</td>
                    <td>{row.client_name ?? '—'}</td>
                    <td>{row.phone ?? '—'}</td>
                    <td>{row.email ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
